import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import {
  HiOutlineClipboardList,
  HiOutlineClock,
  HiOutlineBadgeCheck,
} from "react-icons/hi";
import { HiOutlineMapPin } from "react-icons/hi2";
import { InfoCard } from "@components";
import { OrderStatus } from "../../types/order/IOrder";
import { Order } from "../../models";

interface OrderKPIProps {
  orders: Order[];
  loading: boolean;
  driverId?: string;
  isLocationDetails?: boolean;
}

export const OrderKPIs: FC<OrderKPIProps> = ({
  orders,
  loading,
  driverId,
  isLocationDetails = false,
}) => {
  const { t } = useTranslation(["common", "customer"]);
  const location = useLocation();

  // Beregn KPIs direkte med useMemo - ingen state eller useEffect
  const kpis = useMemo(() => {
    if (!orders) return { total: 0, completed: 0, upcoming: 0, unique: 0 };

    const completed = orders.filter(
      (order) => order.status === OrderStatus.Completed,
    );
    const upcoming = orders.filter(
      (order) =>
        order.status === OrderStatus.New ||
        order.status === OrderStatus.Scheduled,
    );

    return {
      total: orders.length,
      completed: completed.length,
      upcoming: upcoming.length,
      unique: orders.length, // Antall ordrer som bruker denne lokasjonen
    };
  }, [orders]);

  // Generer URL med query-parametere for sjåfør og status
  const getOrdersUrlWithParams = (statuses: OrderStatus[]) => {
    const params = new URLSearchParams();

    if (driverId) {
      params.set("driver", driverId);
    }

    if (statuses.length > 0) {
      params.set("status", statuses.join(","));
    }

    return `/orders?${params.toString()}`;
  };

  // Hent nåværende URL for å bruke som state i lenkene
  const currentPath = location.pathname + location.search;

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 my-4 sm:my-6 w-full">
        <Link
          to={getOrdersUrlWithParams([OrderStatus.Completed])}
          state={{ from: currentPath, driverId: driverId }}
          className="w-full"
        >
          <InfoCard
            icon={
              <HiOutlineBadgeCheck className="h-6 w-6 sm:h-7 sm:w-7 text-secondary" />
            }
            title={t("customer:completed_orders")}
            description={`${kpis.completed}`}
            titleClass="text-secondary text-xs sm:text-sm truncate"
            descriptionClass="text-secondary text-center text-lg sm:text-xl"
            containerClass="bg-success-muted hover:shadow-md transition-shadow duration-200 cursor-pointer p-2 sm:p-3 h-full flex flex-col items-center"
            loading={loading}
          />
        </Link>
        <Link
          to={getOrdersUrlWithParams([OrderStatus.New, OrderStatus.Scheduled])}
          state={{ from: currentPath, driverId: driverId }}
          className="w-full"
        >
          <InfoCard
            icon={
              <HiOutlineClock className="h-6 w-6 sm:h-7 sm:w-7 text-secondary" />
            }
            title={t("customer:upcoming_orders")}
            description={`${kpis.upcoming}`}
            titleClass="text-secondary text-xs sm:text-sm truncate"
            descriptionClass="text-secondary text-center text-lg sm:text-xl"
            containerClass="bg-warning-muted hover:shadow-md transition-shadow duration-200 cursor-pointer p-2 sm:p-3 h-full flex flex-col items-center"
            loading={loading}
          />
        </Link>
        {!isLocationDetails && (
          <Link
            to="/resources/locations"
            state={{ from: currentPath, driverId: driverId }}
            className="w-full"
          >
            <InfoCard
              icon={
                <HiOutlineMapPin className="h-6 w-6 sm:h-7 sm:w-7 text-secondary" />
              }
              title={t("customer:unique_locations", {
                defaultValue: "Lokasjoner",
              })}
              description={`${kpis.unique}`}
              titleClass="text-secondary text-xs sm:text-sm truncate"
              descriptionClass="text-secondary text-center text-lg sm:text-xl"
              containerClass="bg-brand-muted hover:shadow-md transition-shadow duration-200 cursor-pointer"
              loading={loading}
            />
          </Link>
        )}
        <Link
          to={getOrdersUrlWithParams([])}
          state={{ from: currentPath, driverId: driverId }}
          className="w-full"
        >
          <InfoCard
            icon={
              <HiOutlineClipboardList className="h-6 w-6 sm:h-7 sm:w-7 text-secondary" />
            }
            title={t("customer:total_orders")}
            description={`${kpis.total}`}
            titleClass="text-secondary text-xs sm:text-sm truncate"
            descriptionClass="text-secondary text-center text-lg sm:text-xl"
            containerClass="bg-surface-muted hover:shadow-md transition-shadow duration-200 cursor-pointer p-2 sm:p-3 h-full flex flex-col items-center"
            loading={loading}
          />
        </Link>
      </div>
    </>
  );
};
